import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePrivateMutation as useMutation } from "@/lib/query/core/use-private-mutation";
import type {
  AdminStoreControlsView,
  AdminStoreDeleteResult,
  AdminStoreDetailsInput,
  AdminStoreForceDeleteResult,
  AdminStoreLifecycleResult,
  StoreAdminDetailResponse,
} from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";

/**
 * The admin store controls (2026-10-25): status, edit details / slug,
 * deactivate / reactivate and delete forever. Every mutation refreshes the
 * whole `["admin", "stores"]` family — the list's status column, the detail
 * page and this card all read from it.
 */
export const storeControlsQueryKey = (storeId: string) =>
  ["admin", "stores", storeId, "controls"] as const;

export function useStoreControlsQuery(storeId: string) {
  return useQuery({
    queryKey: storeControlsQueryKey(storeId),
    queryFn: () =>
      apiJsonClient<AdminStoreControlsView>(
        endpoints.admin.storeControls(storeId),
      ),
    enabled: storeId.length > 0,
  });
}

const json = (body: unknown): RequestInit => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

function useInvalidateStores() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "stores"] });
}

export function useUpdateStoreDetailsMutation() {
  const invalidate = useInvalidateStores();
  return useMutation({
    mutationFn: ({
      storeId,
      input,
    }: {
      storeId: string;
      input: AdminStoreDetailsInput;
    }) =>
      apiJsonClient<StoreAdminDetailResponse & { revalidated: boolean }>(
        endpoints.admin.storeById(storeId),
        { ...json(input), method: "PATCH" },
      ),
    onSuccess: invalidate,
  });
}

export function useStoreLifecycleMutation() {
  const invalidate = useInvalidateStores();
  return useMutation({
    mutationFn: ({
      storeId,
      action,
      reason,
    }: {
      storeId: string;
      action: "deactivate" | "reactivate";
      reason: string | null;
    }) =>
      apiJsonClient<AdminStoreLifecycleResult>(
        action === "deactivate"
          ? endpoints.admin.storeDeactivate(storeId)
          : endpoints.admin.storeReactivate(storeId),
        json({ reason }),
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteStoreMutation(options: { force?: boolean } = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      storeId,
      confirmSlug,
      reason,
    }: {
      storeId: string;
      confirmSlug: string;
      reason: string | null;
    }) =>
      options.force
        ? apiJsonClient<AdminStoreForceDeleteResult>(
            endpoints.admin.storeForceDelete(storeId),
            json({ confirmSlug, reason }),
          )
        : apiJsonClient<AdminStoreDeleteResult>(
            endpoints.admin.storeDelete(storeId),
            json({ confirmSlug, reason }),
          ),
    onSuccess: (_data, variables) => {
      // The detail queries of a deleted store would 404 on refetch: drop them,
      // then refresh the lists.
      queryClient.removeQueries({
        queryKey: ["admin", "stores", variables.storeId],
      });
      return queryClient.invalidateQueries({ queryKey: ["admin", "stores"] });
    },
  });
}

type SignedUpload = { uploadUrl: string; objectKey: string };

/** Presigned PUT for a store logo; the caller then saves `objectKey`. */
export async function uploadStoreLogo(
  storeId: string,
  file: File,
): Promise<string> {
  const intent = await apiJsonClient<SignedUpload>(
    endpoints.admin.storeLogoUpload(storeId),
    json({ fileName: file.name, contentType: file.type, sizeBytes: file.size }),
  );
  const put = await fetch(intent.uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  if (!put.ok) throw new Error(`Logo upload failed (${put.status})`);
  return intent.objectKey;
}
